import { Header } from '../../../components/layout/header';
import { Main } from '../../../components/layout/main';
import { Button } from '../../../components/ui/button';
import { LogOut, EyeIcon, Pencil } from 'lucide-react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth';
import TableCustomer from '../../../components/table';
import { UserRole } from '../../../constants/user';
import useUsers from '../../../hooks/user';
import SelectComponent from '../../../components/select';
import { useCallback, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { SubmitHandler, FieldValues, useForm } from 'react-hook-form';
import DropdownCheckbox from '../../../components/dropdown-checkbox';
import UpdateUser from './components/update.user';
import CreateUser from './components/create.user';
import InlineEdit from './components/inline-edit';

const columns = [
    { value: '_id', label: 'ID' },
    {
        value: 'avatarUrl',
        label: 'Avatar',
        render: ({ data }: { data: any }) => (
            <div className="flex justify-center">
                <img
                    src={data.avatarUrl || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(data.name)}
                    alt="avatar"
                    className="w-8 h-8 rounded-full object-cover"
                />
            </div>
        )
    },
    { value: 'name', label: 'Full Name' },
    { value: 'email', label: 'Email' },
    { value: 'role', label: 'Role' },
    { value: 'isWorking', label: 'Working Status' },
    { value: 'allowedManualLog', label: 'Allow Manual Log' },
    { value: 'allowedOT', label: 'Allow Overtime' },
    { value: 'allowedWorkAtWeekend', label: 'Work on Weekend' },
    { value: 'allowedWorkAtNoonTime', label: 'Work at Noon' },
    { value: 'slackId', label: 'Slack ID' },
    { value: 'allowedWebTracking', label: 'Web Tracking' },
    { value: 'deactivated', label: 'Deactivated' },
    { value: 'joinDate', label: 'Join Date' },
    { value: 'position', label: 'Position' },
    { value: 'skipScreenshot', label: 'Skip Screenshot' },
    { value: 'skipCheckAutoClick', label: 'Skip check auto click' },
    { value: 'skipActivityCheck', label: 'Skip activity check' },
    { value: 'skipIdenticalImagesCheck', label: 'Skip identical images check' },
    { value: 'skipHeartbeatCheck', label: 'Skip heartbeat check' },
    { value: 'birthDate', label: 'Birth Date' },
    { value: 'phoneNumber', label: 'Phone Number' },
    { value: 'description', label: 'Description' },
    { value: 'dailyTrackingTimeLimit', label: 'Daily Tracking Time Limit' },
    { value: 'screenshotIntervalMinutes', label: 'Screenshot Interval' },
    {
        value: 'activityStatistics',
        label: 'Activity Statistics',
        render: ({ data }: { data: any }) => {
            return <div className='flex justify-center items-center'>
                <Link to={`/activity-statistics/${data._id}`} className='border border-gray-300 rounded-md p-1'>
                    <EyeIcon color='gray' width={18} height={18} />
                </Link>
            </div>
        }
    }
];

const User = () => {
    const { logout } = useAuthStore();

    const { isLoading, selected, handleSelect, handleSelectAll, data, setParams, params } = useUsers();

    const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);

    const handleSetRole = useCallback((value: string) => {
        setParams(prev => ({ ...prev, role: value }));
    }, [setParams])

    const handleSetIsWorking = useCallback((value: string) => {
        setParams(prev => ({ ...prev, isWorking: value }));
    }, [setParams])

    const { register, handleSubmit } = useForm();

    const onSubmit: SubmitHandler<FieldValues> = useCallback((data) => {
        setParams(prev => ({ ...prev, search: data.search }));
    }, [setParams])

    const handleSetDeactivated = useCallback((value: string) => {
        setParams(prev => ({ ...prev, deactivated: value === 'true' }));
    }, [setParams])

    const [hideColumn, setHideColumn] = useState<string[]>(['deactivated', '_id', 'allowedWorkAtNoonTime', 'birthDate', 'description', 'dailyTrackingTimeLimit', 'activityStatistics', 'phoneNumber', 'joinDate', 'position', 'allowedWebTracking', 'skipCheckAutoClick', 'skipActivityCheck', 'skipIdenticalImagesCheck', 'skipHeartbeatCheck']);

    const isActivityStatisticsPage = useLocation().pathname.includes('activity-statistics');

    const handleEditClick = (rowId: string, field: string) => {
        setEditingCell({ rowId, field });
    };

    const handleCancelEdit = () => {
        setEditingCell(null);
    };

    const renderCell = (row: any, field: string) => {
        if (editingCell?.rowId === row._id && editingCell?.field === field) {
            return (
                <InlineEdit
                    data={row}
                    field={field}
                    onCancel={handleCancelEdit}
                />
            );
        }

        const value = row[field];
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        return value;
    };

    const columnsWithEdit = columns.map(col => ({
        ...col,
        render: col.render || (({ data }: { data: any }) => (
            <div className="flex items-center gap-2">
                {renderCell(data, col.value)}
                {!['_id', 'avatarUrl', 'activityStatistics'].includes(col.value) && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditClick(data._id, col.value)}
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                )}
            </div>
        ))
    }));

    return (
        <>
            <Header fixed className='flex justify-between w-[inherit]'>
                <div className='mr-0 flex items-center justify-end space-x-4 w-[inherit]'>
                    <Button variant="outline" size="icon" onClick={logout}>
                        <LogOut />
                    </Button>
                </div>
            </Header>
            <Main>
                <div className='flex justify-between items-center mb-4'>
                    <h1 className='text-2xl font-bold'>User Management</h1>
                    {!isActivityStatisticsPage && <CreateUser />}
                </div>
                {
                    !isActivityStatisticsPage &&
                    <>
                        <div className='flex items-center justify-between flex-wrap'>
                            <div>
                                <p className='text-sm font-medium'>Search</p>
                                <form className='flex items-center space-x-2' onSubmit={handleSubmit(onSubmit)}>
                                    <Input placeholder='Search by name or email' {...register('search')} />
                                    <Button type='submit'>Search</Button>
                                </form>
                            </div>
                            <div>
                                <p className='text-sm font-medium'>Working Status</p>
                                <SelectComponent
                                    options={[
                                        { label: 'All', value: 'all' },
                                        { label: 'Working', value: 'true' },
                                        { label: 'Not Working', value: 'false' }
                                    ]}
                                    onChange={handleSetIsWorking}
                                    placeholder='Select working status'
                                    defaultValue={params.isWorking}
                                />
                            </div>
                            <div>
                                <p className='text-sm font-medium'>Role</p>
                                <SelectComponent
                                    options={[
                                        { label: 'All', value: 'all' },
                                        { label: 'Admin', value: UserRole.ADMIN },
                                        { label: 'Manager', value: UserRole.MANAGER },
                                        { label: 'Employee', value: UserRole.EMPLOYEE }
                                    ]}
                                    onChange={handleSetRole}
                                    placeholder='Select role'
                                    defaultValue={params.role}
                                />
                            </div>

                            <div>
                                <p className='text-sm font-medium'>Account Status</p>
                                <SelectComponent
                                    options={[
                                        { label: 'Active', value: 'false' },
                                        { label: 'Inactive', value: 'true' }
                                    ]}
                                    onChange={handleSetDeactivated}
                                    placeholder='Select account status'
                                    defaultValue={params.deactivated ? 'true' : 'false'}
                                />
                            </div>

                            <div>
                                <p className='text-sm font-medium'>Hide Columns</p>
                                <DropdownCheckbox options={columns} selected={hideColumn} setSelected={setHideColumn} />
                            </div>
                        </div>
                        <TableCustomer
                            columns={columnsWithEdit}
                            data={data?.users || []}
                            loading={isLoading}
                            hideColumn={hideColumn}
                            selected={selected}
                            handleSelect={handleSelect}
                            handleSelectAll={handleSelectAll}
                            Update={UpdateUser}
                            pagination={{
                                currentPage: data?.page || 1,
                                totalPages: Math.ceil((data?.total || 0) / (data?.limit || 10)),
                                totalItems: data?.total || 0,
                                itemsPerPage: data?.limit || 10,
                                onPageChange: (page: number) => {
                                    setParams(prev => ({ ...prev, page }));
                                }
                            }}
                        />
                    </>
                }
                <Outlet />
            </Main>
        </>
    )
}

export default User;